import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/uxp-photoshop/uxp-photoshop/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "batchplay"
    }}>{`batchPlay`}</h1>
    <p>{`At the heart of our APIs is `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`, a method that executes one or more Photoshop action commands and return their results.`}</p>
    <p><em parentName="p">{`batchPlay is an advanced API, and we recommend trying to first use the DOM APIs before resorting to using batchPlay.`}</em></p>
    <h2 {...{
      "id": "api"
    }}>{`API`}</h2>
    <p>{`BatchPlay can directly be accessed from the Photoshop action module, and as most other APIs it is asynchronous by default and returns a promise.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('photoshop').action.batchPlay(descriptors: ActionDescriptor[], options: Object): Promise<Object[]>
`}</code></pre>
    <p>{`Below we will dive into both how action commands are defined in JavaScript, and what options are available.`}</p>
    <h3 {...{
      "id": "descriptors"
    }}>{`Descriptors`}</h3>
    <p>{`The first argument to `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` is an array of descriptors (also called action descriptors).`}</p>
    <p>{`UXP plugins for Photoshop use a textual representation of these action descriptors. This format is sometimes referred to as `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{`.`}</p>
    <p>{`An action descriptor contains the following components:`}</p>
    <ul>
      <li parentName="ul">{`The command. This describes the desired Photoshop command. The command is specified via the `}<inlineCode parentName="li">{`_obj`}</inlineCode>{` keyword.`}</li>
      <li parentName="ul">{`The target for the command. This describes the (DOM) element that the command should operate on. The target is specified via the `}<inlineCode parentName="li">{`_target`}</inlineCode>{` keyword. This property is sometimes omitted. If omitted, the command operates on a default element. The default element is typically the object that is active in the UI.`}</li>
      <li parentName="ul">{`Command parameters. Describes the various parameters for the command.`}</li>
      <li parentName="ul">{`Command execution options. Options related to how the command should be executed.`}</li>
    </ul>
    <p>{`The following is an example of an `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{` command for hiding a layer `}<a id="hidelayerexample"></a>{`:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`{
  _obj: "hide",
  _target:[
    {_ref: "layer", _enum: "ordinal", _value: "targetEnum"},
    {_ref: "document", _enum: "ordinal", _value: "targetEnum"}
  ]
}
`}</code></pre>
    <p>{`The command is `}<inlineCode parentName="p">{`hide`}</inlineCode>{` as specified by the `}<inlineCode parentName="p">{`_obj`}</inlineCode>{` keyword.`}</p>
    <p>{`The value of `}<inlineCode parentName="p">{`_target`}</inlineCode>{` is called an `}<inlineCode parentName="p">{`action reference`}</inlineCode>{` and specifies which layer should be hidden. The action reference specifies the target in reverse order - from most specific to least (similar to a postal address). To find the DOM object for a target you go from the last value in the list to the first. In this example we have:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`{"_ref":"document", "_enum":"ordinal","_value":"targetEnum"}`}</inlineCode>{` First find the target document. It is specified as `}<inlineCode parentName="li">{`ordinal`}</inlineCode>{` and `}<inlineCode parentName="li">{`targetEnum`}</inlineCode>{`. This means the active (front most) document.`}</li>
      <li parentName="ul">{`In that document we look for the layer specified as `}<inlineCode parentName="li">{`{"_ref":"layer","_enum":"ordinal","_value":"targetEnum"}`}</inlineCode>{`. This means the active (selected) layer.`}</li>
    </ul>
    <p>{`For this command we are therefore targeting the selected layer in the front most document. `}<inlineCode parentName="p">{`targetEnum`}</inlineCode>{` is the default value for ordinal enumerations and can be omitted. See the section on `}<a parentName="p" {...{
        "href": "#action-references"
      }}>{`action references`}</a>{` below for details.`}</p>
    <p>{`This command modifies the Photoshop state and must therefore be executed from within a modal scope. See the documentation of `}<a parentName="p" {...{
        "href": "./executeasmodal"
      }}>{`executeAsModal`}</a>{` for details. The following JavaScript uses batchPlay to hide the active layer:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function hideActiveLayer() {
  return await require("photoshop").action.batchPlay([{_obj:"hide", _target:[{_ref: "layer", _enum: "ordinal"},{_ref: "document", _enum: "ordinal"}]}], {});
}
let result = require("photoshop").core.executeAsModal(hideActiveLayer, {"commandName": "Hide Layer"});
`}</code></pre>
    <p>{`Here is another example of `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` and `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{`. This examples returns the value of a specific document pixel.
This example illustrates providing `}<inlineCode parentName="p">{`samplePoint`}</inlineCode>{` as an additional command parameter.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function samplePixel() {
  return await require("photoshop").action.batchPlay([{
    _obj: "colorSampler",
    _target: {_ref: "document",_enum: "ordinal",_value: "targetEnum"},
    samplePoint: {
      horizontal: 100,
      vertical: 100
    }
  }], {});
}
let result = await require("photoshop").core.executeAsModal(samplePixel, {"commandName": "Sample Pixel"});
`}</code></pre>
    <p>{`Sample output from the colorSampler command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"colorSampler":{"_obj":"CMYKColorClass","black":0,"cyan":26.27,"magenta":4.71,"yellowColor":0},"sampledData":true}]
`}</code></pre>
    <h4 {...{
      "id": "accepted-action-descriptors"
    }}>{`Accepted action descriptors`}</h4>
    <p>{`The composition of action descriptors can be complicated. Photoshop provides a number of ways that help a developer understand which descriptors Photoshop accepts.`}</p>
    <p>{`The first way is to use the `}<inlineCode parentName="p">{`Actions`}</inlineCode>{` panel. Create a new action and record the commands that you want to run from JavaScript. Then select the action in the `}<inlineCode parentName="p">{`Actions`}</inlineCode>{` panel, or one or more individual steps in the action. Now select `}<inlineCode parentName="p">{`Copy As JavaScript`}</inlineCode>{` from the panel flyout menu. This will copy UXP compatible JavaScript for the selected items to the clipboard. `}<inlineCode parentName="p">{`Copy As JavaScript`}</inlineCode>{` is available from the flyout menu when developer mode is enabled.
The following is an example of copying a single "make layer" command as JavaScript:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function actionCommands() {
    let command;
    let result;
    let psAction = require("photoshop").action;

    // Make layer
    command = {"_obj":"make","_target":[{"_ref":"layer"}],"layerID":2};
    result = await psAction.batchPlay([command], {});
}

async function runModalFunction() {
    await require("photoshop").core.executeAsModal(actionCommands, {"commandName": "Action Commands"});
}

await runModalFunction();
`}</code></pre>
    <p>{`Another option is to create a listener function in JavaScript. This is done by providing the global event hook to a low level API. This call only works when developer mode is enabled.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require('photoshop').action.addNotificationListener(['all'], (event, descriptor) => {console.log("Event:" + event + " Descriptor: " + JSON.stringify(descriptor))});
`}</code></pre>
    <p>{`Another option is to use the developer UI to log action descriptors to a file. When "developer mode" is enabled, then the following menu items will be available:`}</p>
    <ul>
      <li parentName="ul">{`"Plugins > Development > Record Action Commands..." This menu item can be used to save any Photoshop command as an action descriptor to a file. After selecting the menu item and selecting the destination file, perform one of more Photoshop commands using the normal UI. Then choose "Plugins > Development > Stop Action Recording". The destination file will contain actionJSON for the performed commands.`}</li>
      <li parentName="ul">{`"Plugins > Development > Record Action Notifications..." This menu item will save both commands and change notifications to the selected destination file. `}</li>
    </ul>
    <p>{`Lastly, you can record your actions as a standard Photoshop action using the Actions panel. Then export actions to an `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{` format by doing the following:`}</p>
    <ul>
      <li parentName="ul">{`Select the action set that contains the command.`}</li>
      <li parentName="ul">{`Press (macOS)"shift+option+command" or (Windows)"shift+control+alt" and choose "Save Actions..." from the panel fly-out menu.`}</li>
    </ul>
    <h4 {...{
      "id": "command-execution-options"
    }}>{`Command execution options`}</h4>
    <p>{`An action descriptor can include a `}<inlineCode parentName="p">{`_options`}</inlineCode>{` property. If present then its value specifies how the command is executed.
`}<inlineCode parentName="p">{`_options`}</inlineCode>{` can contain the following values:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`dialogOptions: string`}</inlineCode>{`. This value controls how Photoshop shows UI during the execution of the command. The possible values are:`}
        <ul parentName="li">
          <li parentName="ul">{`"silent": The command is executed without UI. If an error occurs, or if the command needs additional parameters, then a scripting error is returned. This is the default value.`}</li>
          <li parentName="ul">{`"dontDisplay": The command is executed without UI unless an error occurs, or if the command need additional parameters. In that case UI may be shown.`}</li>
          <li parentName="ul">{`"display": Standard UI related to the command is shown.`}</li>
        </ul>
      </li>
    </ul>
    <h3 {...{
      "id": "options"
    }}>{`options`}</h3>
    <p>{`The second argument of batchPlay adjusts the options. Below is the list of options that are most commonly needed.`}</p>
    <h4 {...{
      "id": "synchronousexecution-default-false"
    }}>{`synchronousExecution (default: false)`}</h4>
    <p>{`If set to true, batchPlay will block the entire scripting thread until it resolves, then return the value(s). We use this in the DOM API for property getters and setters, as it allows for simpler code.`}</p>
    <p>{`JavaScript code that use batchPlay directly should avoid using this keyword if possible, and instead use the default form that returns a promise.`}</p>
    <h4 {...{
      "id": "historystateinfo-default-none"
    }}>{`historyStateInfo (default: none)`}</h4>
    <p>{`This option is deprecated as of Photoshop 2022. New JavaScript code should use the history suspension mechanism provided by `}<a parentName="p" {...{
        "href": "./executeasmodal#history-state-suspension"
      }}>{`executeAsModal`}</a></p>
    <p>{`Request Photoshop to describe the entire batchPlayed series of actions as a single history state. History state should have two properties:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`name`}</inlineCode>{`: A string to show the name of the history event in History panel`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li"><inlineCode parentName="p">{`target`}</inlineCode>{`: A document reference for where to create the history state at. Keep in mind that if you point at a different document, or if your batchPlays aren't all acting on the same document, the behavior is undefined.`}</p>
        <p parentName="li">{`Plugins that use api version 2 may find it easier to use the history state functionality that is provided via the executionContext object.`}</p>
      </li>
    </ul>
    <h3 {...{
      "id": "result-value"
    }}>{`Result value`}</h3>
    <p><inlineCode parentName="p">{`batchPlay`}</inlineCode>{` returns a promise. This promise is rejected if the batchPlay command is invalid. This is the case when incorrect arguments are provided. An example of a batchPlay rejection is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`require("photoshop").action.batchPlay(true, {});
`}</code></pre>
    <p>{`This example provides an incorrect initial argument to batchPlay. The initial argument is expected to be a descriptor list and not a boolean. For cases such as this, the promise is rejected with an appropriate error message. The above example generates the following rejection:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[[PromiseState]]: "rejected"
[[PromiseResult]]: Error: Argument 1 has an invalid type. Expected type: array actual type: boolean
`}</code></pre>
    <p>{`A batchPlay command can also fail if a valid command cannot be procesed by Photoshop. This is most often the case if the specified target element does not exist.`}</p>
    <p>{`The previously shown example to `}<a parentName="p" {...{
        "href": "#hidelayerexample"
      }}>{`hide the active layer`}</a>{` will fail if no documents are open, because the command can then not find the target layer to hide. For such cases the `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` promise is resolved successfully, and an error message is returned in a list form.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[
  {
  _obj: "error"
  message: "The object “current document” is not currently available."
  result: -25922
  }
]
`}</code></pre>
    <p>{`The elements in the returned list match the action descriptor list passed to batchPlay. The first item in the returned list is the result of the first action descriptor in the batchPlay, and so forth. The properties of the returned elements are:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`_obj: string`}</inlineCode>{`. The value is "Error"`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`message: string`}</inlineCode>{`. A localized error message`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`result: number`}</inlineCode>{`. An internal Photoshop error code. A "0" means "no error". -128 means that the user cancelled the operation. Other values are possible.`}</li>
    </ul>
    <h3 {...{
      "id": "action-references"
    }}>{`Action references`}</h3>
    <p>{`Action references specify which DOM objects a command operates on. An action reference is specified using the `}<inlineCode parentName="p">{`_target`}</inlineCode>{` keyword.
An action reference is a list of item references that specifies how to find the DOM element starting from the application DOM element.
Each item in the list can use one of the following forms:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Reference form`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Syntax`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Example`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Notes`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`ID`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:className, _id:number}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:"document", _id: 123}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Index`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:className, _index:number}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:"document", _index: 1}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Indices are 1 based`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Name`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:className, _name:string}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:"document", _name: "Untitled-1"}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Enumeration`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:className, _enum: "ordinal", _value: enumerationSpecifier*}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_ref:"document", _enum: "ordinal", _value:"targetEnum"}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`Property`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_property:propertyName}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`{_property:"title"}`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}></td>
        </tr>
      </tbody>
    </table>
    <p>{`We recommend using the ID form whenever possible because the ID of an object does not change during the lifetime of the Photoshop session. The index will change if elements are added or removed in front of the specified object.`}</p>
    <p>{`The enumerationSpecifier is command specific, but the most common value is `}<inlineCode parentName="p">{`targetEnum`}</inlineCode>{` meaning the currently selected or active object of the specified class. Other possible values include: "first", "last", and "front". When using the enumeration form, `}<inlineCode parentName="p">{`targetEnum`}</inlineCode>{` is the default value for the `}<inlineCode parentName="p">{`_value`}</inlineCode>{` property.`}</p>
    <h3 {...{
      "id": "getting-state-from-photoshop"
    }}>{`Getting state from Photoshop`}</h3>
    <p>{`batchPlay can be used to obtain state from Photoshop. To do this, use the action command "get" with a target property.`}</p>
    <p>{`The following sample obtains the title of a target document.:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var target = {_ref:[{"_property": "title"}, {_ref: "document", _id: someDocumentID}, {"_ref":"application"}]};
var command = {"_obj": "get", "_target": target};
let result = await photoshop.action.batchPlay([command], {});
`}</code></pre>
    <p>{`A possible result from running this command is:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"title":"Untitled-1"}]
`}</code></pre>
    <p>{`If you use the "get" command without a target property, then all possible properties for the target are returned. The following lists all possible document properties:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`var target = {_ref:[{_ref: "document", _id: someDocumentID}, {"_ref":"application"}]};
var command = {"_obj": "get", "_target": target};
let result = await photoshop.action.batchPlay([command], {});
`}</code></pre>
    <p>{`A possible result from running this command may include the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"mode":{"_enum":"colorSpace","_value":"RGBColor"},"bigNudgeH":655360,"bigNudgeV":655360,"rulerOriginH":0,"rulerOriginV":0,"width":{"_unit":"distanceUnit","_value":504},"height":{"_unit":"distanceUnit","_value":360},"resolution":{"_unit":"densityUnit","_value":300},"title":"Untitled-1","fileInfo":{"_obj":"fileInfo"},"numberOfPaths":0,"numberOfChannels":3,"numberOfLayers":0,"targetPathIndex":-1,"workPathIndex":-1,"clippingPathInfo":{"_obj":"clippingInfo","clippingPathIndex":-1,"clippingPathFlatness":0}, . . . ]
`}</code></pre>
    <p>{`Using "get" without a property is intended for use during the development of a plugin. This for allows a developer to understand which properties a given element supports.`}</p>
    <p><em parentName="p">{`A shipping plugin should not obtain all properties for a given target because this may be slow, and may become slower in the future when additional properties are added.`}</em></p>
    <h4 {...{
      "id": "multi-get"
    }}>{`Multi-Get`}</h4>
    <p>{`In some cases you may want to obtain several property values from the same target, or get the same property from several similar targets.
Examples of this are:`}</p>
    <ul>
      <li parentName="ul">{`Get the name, the id, and the color mode for a specific document.`}</li>
      <li parentName="ul">{`Get the id's of all open documents.`}</li>
    </ul>
    <p>{`While you can issue multiple "get" statements to get this information, it is more efficient to use the `}<inlineCode parentName="p">{`multiGet`}</inlineCode>{` command.
A multiGet command has the following form:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`_obj`}</inlineCode>{`: must be "multiGet".`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`_target`}</inlineCode>{`: a target reference that specifies a base object.`}</li>
      <li parentName="ul">{`extendedReference: a list describing what values to return from the base object. The extendedReference has either one or two elements. The first element is a property-list. The second element can be used to specify an element range.`}</li>
      <li parentName="ul">{`options: describes how the command should react if any of the requested values are unavailable. options can include:`}
        <ul parentName="li">
          <li parentName="ul">{`failOnMissingProperty: A boolean describing what should happen if one of the target elements do not expose one of the requested properties. The default value is true, meaning that the command fails if a requested property is not available.`}</li>
          <li parentName="ul">{`failOnMissingElement: A boolean describing what should happen if one of the target elements do exist. The default value is true, meaning that the command fails if any of the requested element do not exist.`}</li>
        </ul>
      </li>
    </ul>
    <p>{`The following example illustrates how to get the value of multiple properties on the active layer:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`layerProperties =
  { _obj: "multiGet",
     _target: { _ref: [{_ref: "layer", _enum: "ordinal"}, {_ref: "document", _enum: "ordinal"}]},
     extendedReference: [["name", "layerID", "opacity"]],
     options: {failOnMissingProperty:false, failOnMissingElement: false}
  };
result = await require("photoshop").action.batchPlay([layerProperties], {});
`}</code></pre>
    <p>{`This command will generate a result such as the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"name":"Layer 4","layerID":5,"opacity":255}]
`}</code></pre>
    <p>{`In this example the base object is a layer. The extendedReference specifies the list of properties to return from the target layer.`}</p>
    <p>{`The following example illustrates how to get the value of multiple properties on the first two layers:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`layerProperties =
  { _obj: "multiGet",
     _target: { _ref: [{_ref: "document", _enum: "ordinal"}]},
     extendedReference: [["name", "layerID", "opacity"], {_obj: "layer", index:1, count:2}],
     options: {failOnMissingProperty:false, failOnMissingElement: false}
  };
result = await require("photoshop").action.batchPlay([layerProperties], {});
`}</code></pre>
    <p>{`This command will generate a result such as the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"list":[{"name":"Layer 1","layerID":2,"opacity":255},{"name":"Layer 2","layerID":3,"opacity":255}]}]
`}</code></pre>
    <p>{`In this example we want to obtain the value from several layers, and this mean that the base object as specified by the `}<inlineCode parentName="p">{`_target`}</inlineCode>{` is a document. The extendedReference is used in its two element form to specify a property list and a range of layers.`}</p>
    <p>{`The element range specifier can use count equal to -1 to specify all elements. Therefore to get the name of all layers in a document, one can use the following multiGet command:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`layerProperties =
  { _obj: "multiGet",
     _target: { _ref: [{_ref: "document", _enum: "ordinal"}]},
     extendedReference: [["name"], {_obj: "layer", index:1, count:-1}],
     options: {failOnMissingProperty:false, failOnMissingElement: false}
  };
result = await require("photoshop").action.batchPlay([layerProperties], {});
`}</code></pre>
    <p>{`This command will generate a result such as the following:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`[{"list":[{"name":"Layer 1"},{"name":"Layer 2"},{"name":"Layer 3"},{"name":"Layer 4"}]}]
`}</code></pre>
    <h2 {...{
      "id": "legacy-notes"
    }}>{`Legacy notes`}</h2>
    <p><inlineCode parentName="p">{`batchPlay`}</inlineCode>{` is the evolution of `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` from ExtendScript. Where `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` could only play one descriptor at a time, `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{` accepts an array of action descriptors.`}</p>
    <p>{`In ExtendScript, we provided a class around constructing descriptors, references, and putting values in. With `}<inlineCode parentName="p">{`batchPlay`}</inlineCode>{`, we have replaced these related classes with `}<inlineCode parentName="p">{`actionJSON`}</inlineCode>{`.`}</p>
    <p>{`If you have used `}<inlineCode parentName="p">{`executeAction`}</inlineCode>{` in ExtendScript, you are probably familiar with 4 character codes (OSTypes) and helper methods around them. In `}<inlineCode parentName="p">{`actionJson`}</inlineCode>{` we prefer extended string identifiers such as `}<inlineCode parentName="p">{`colorSampler`}</inlineCode>{`. However, you can still use an OSType by prepending it with a `}<inlineCode parentName="p">{`$`}</inlineCode>{` sign and passing that as a string, like `}<inlineCode parentName="p">{`'$app '`}</inlineCode>{` (mind the space!).`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      